import {
  faCheck,
  faChevronRight,
  faCircleNotch,
  faDoorOpen,
  faExclamationCircle,
  faGear,
  faLanguage,
  faMoon,
  faPencil,
  faQrcode,
  faShareAlt,
  faSun,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AuthContext from "context/AuthContext";
import { Badge } from "flowbite-react";
import useToastContext from "hooks/useToastContext";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPut } from "utils/apiFetch";
import pathObj from "utils/apiPath";
import LanguageSelect from "./LanguageSelect";
import BottomSheet from "./reusable/BottomSheet";

export default function ProfileMenu({ onClose }) {
  const { user, logoutUser, switchAccount, accounts, setAccounts, getProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showLanguageBottomSheet, setShowLanguageBottomSheet] = useState(false);
  const [showAccountsListBottomSheet, setShowAccountsListBottomSheet] = useState(false);

  const getMyAccounts = async () => {
    try {
      if (accounts.length > 0) return;

      var response = await apiGet(pathObj.getProfile);
      if (response.data.success) {
        setAccounts(response.data.results.accounts);
      }
    } catch (err) {}
  };

  useEffect(() => {
    getMyAccounts();
  }, []);

  const profilePicRef = useRef(null);
  const [updatingPFP, setUpdatingPFP] = useState(false);
  const notification = useToastContext();
  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUpdatingPFP(true);
    try {
      const formData = new FormData();
      formData.append("user_image", file);
      let config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      const response = await apiPut(pathObj.updateProfile, formData, config);
      if (response.data.success) {
        await getProfile();
      } else {
        notification.error(response.data.message);
      }
    } catch (err) {}
    setUpdatingPFP(false);
  };

  return (
    <>
      <div className="flex justify-between items-center mx-3">
        <div className="flex gap-2 items-center justify-start">
          <input type="file" className="hidden" ref={profilePicRef} accept="image/png, image/jpg, image/jpeg" onChange={handleProfilePicChange} />
          {updatingPFP ? (
            <div className="w-12 h-12 rounded-full bg-primary flex justify-center items-center">
              <FontAwesomeIcon icon={faCircleNotch} className="text-white text-xl animate-spin" />
            </div>
          ) : (
            <img
              className="w-12 h-12 rounded-full cursor-pointer object-cover"
              onClick={() => {
                profilePicRef.current.click();
              }}
              src={user.profile_pic}
              alt=""
            />
          )}
          <div className="font-medium">
            <p className="text-lg md:text-base m-0">
              {user.first_name} {user.last_name}
            </p>
            <span className="text-xs">@{user.username}</span>
          </div>
        </div>
        <FontAwesomeIcon onClick={() => {
          onClose()
          navigate('/edit-profile')
        }} icon={faPencil} size={"lg"} className="cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-full p-2" />
      </div>

      <div className="flex justify-between items-center p-2 mt-3">
        <div className="flex justify-between items-center gap-2">
          <FontAwesomeIcon icon={faUser} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("ACCOUNT_TYPE")}</span>
        </div>
        <span className="font-medium text-lg md:text-base">{user.userType === "individual" ? t("INDIVIDUAL") : t("MERCHANT")}</span>
      </div>

      {accounts.length > 1 && (
        <>
          <div
            className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg"
            onClick={() => {
              setShowAccountsListBottomSheet(true);
            }}
          >
            <div className="flex justify-between items-center gap-2">
              <FontAwesomeIcon icon={faUsers} size={"lg"} />
              <span className="font-medium text-lg md:text-base">{t("O_MY_ACCOUNTS")}</span>
              <Badge className="rounded-full">{accounts.length}</Badge>
            </div>
            <FontAwesomeIcon icon={faChevronRight} size={"lg"} />
          </div>
        </>
      )}

      <div
        className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg"
        onClick={() => {
          onClose();
          navigate("/mycode");
        }}
      >
        <div className="flex justify-between items-center gap-2">
          <FontAwesomeIcon icon={faQrcode} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("O_MY_QR_CODE")}</span>
        </div>
        <FontAwesomeIcon icon={faChevronRight} size={"lg"} />
      </div>

      <div
        className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg"
        onClick={() => {
          onClose();
          navigate("/referral");
        }}
      >
        <div className="flex justify-between items-center gap-2">
          <FontAwesomeIcon icon={faShareAlt} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("NAV_REFER_AND_EARN")}</span>
        </div>
        <FontAwesomeIcon icon={faChevronRight} size={"lg"} />
      </div>

      <div
        className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg"
        onClick={() => {
          setShowLanguageBottomSheet(true);
        }}
      >
        <div className="flex justify-between items-center gap-2">
          <FontAwesomeIcon icon={faLanguage} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("O_LANGUAGE")}</span>
        </div>
        <FontAwesomeIcon icon={faChevronRight} size={"lg"} />
      </div>

      <div
        className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg"
        onClick={() => {
          window.open("https://nsolution.3cx.eu/callus/#LiveChat111672", "_blank");
        }}
      >
        <div className="flex justify-between items-center gap-2">
          <FontAwesomeIcon icon={faExclamationCircle} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("Support")}</span>
        </div>
        <FontAwesomeIcon icon={faChevronRight} size={"lg"} />
      </div>

      <div className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg">
        <div
          className="flex justify-between items-center gap-2"
          onClick={() => {
            onClose();
            navigate("/settings");
          }}
        >
          <FontAwesomeIcon icon={faGear} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("NAV_SETTINGS")}</span>
        </div>
        <FontAwesomeIcon
          icon={faSun}
          className="hidden dark:block"
          size={"lg"}
          onClick={() => {
            document.documentElement.classList.toggle("dark");
            localStorage.setItem("theme", "light");
          }}
        />
        <FontAwesomeIcon
          icon={faMoon}
          className="block dark:hidden"
          size={"lg"}
          onClick={() => {
            document.documentElement.classList.toggle("dark");
            localStorage.setItem("theme", "dark");
          }}
        />
      </div>
      <div
        className="flex justify-between items-center p-2 cursor-pointer active:hover:text-white active:hover:bg-[var(--card-color)] rounded-lg"
        onClick={() => {
          onClose();
          logoutUser();
        }}
      >
        <div className="flex justify-between items-center gap-2">
          <FontAwesomeIcon icon={faDoorOpen} size={"lg"} />
          <span className="font-medium text-lg md:text-base">{t("Exit")}</span>
        </div>
        <FontAwesomeIcon icon={faChevronRight} size={"lg"} />
      </div>
      {showLanguageBottomSheet && (
        <LanguageSelect
          onClose={() => {
            setShowLanguageBottomSheet(false);
          }}
        />
      )}

      {showAccountsListBottomSheet && (
        <BottomSheet
          title={t("O_MY_ACCOUNTS")}
          open={true}
          onClose={() => {
            setShowAccountsListBottomSheet(false);
          }}
        >
          {accounts.map((it) => (
            <div
              key={it._id}
              className="bg-card text-white rounded-lg p-2 px-4 flex justify-between items-center gap-3 mb-3 cursor-pointer"
              onClick={() => {
                setShowAccountsListBottomSheet(false);
                switchAccount(it._id);
                onClose();
              }}
            >
              <div className="flex flex-col font-medium">
                <div>
                  <span>{it.accountName}</span> · <span>{it.account_type}</span>
                </div>
                <span>{it.account_number}</span>
              </div>

              <FontAwesomeIcon icon={faCheck} className={`card-icon ${user._id === it._id ? "block" : "invisible"}`} size="xl" />
            </div>
          ))}
        </BottomSheet>
      )}
    </>
  );
}
